import { createTheme } from '@material-ui/core/styles';
import { MuiPickersOverrides } from '@material-ui/pickers/typings/overrides';
import { Overrides } from '@material-ui/core/styles/overrides';
import * as tokens from '@utmost.co/utmost-design-tokens';

const DEFAULT_FONT_SIZE = 14;

const MuiLabel = {
  root: {
    color: '#000',
    fontWeight: 500,
    display: 'inline-block',
    fontSize: 14,
    textTransform: 'uppercase',
    transform: 'initial',
    '&$focused': {
      color: tokens.colorPrimary
    },
    '&$required': {
      '& span:not(.info-label)': {
        color: tokens.colorAlert
      }
    }
  },
  shrink: {
    transform: 'initial',
    position: 'static'
  }
};

export const frontdoorTheme = createTheme({
  palette: {
    text: {
      primary: tokens.source.colorOnSurface,
      secondary: tokens.source.colorOnSurfaceVariant
    },
    primary: {
      main: tokens.source.colorPrimary
    },
    common: {
      black: tokens.source.colorOnSurface
    }
  },
  typography: {
    fontSize: DEFAULT_FONT_SIZE
  },
  overrides: {
    MuiSelect: {
      select: {
        fontSize: DEFAULT_FONT_SIZE,
        color: tokens.colorOnSurface,
        fontWeight: 'normal'
      }
    },
    MuiPaper: {
      root: {
        border: `1px solid ${tokens.colorSurfaceVariant}`
      }
    },
    MuiPopover: {
      root: {
        '& .MuiInputBase-root': {
          backgroundColor: tokens.colorSurfaceVariant,
          borderColor: 'transparent'
        }
      }
    },
    MuiMenu: {
      list: {
        maxHeight: '300px'
      }
    },
    MuiMenuItem: {
      root: {
        fontSize: DEFAULT_FONT_SIZE,
        backgroundColor: tokens.colorSurface,
        color: tokens.colorOnSurface,
        padding: '12px 16px !important',
        minHeight: '46px !important',
        justifyContent: 'space-between',
        '&:hover': {
          backgroundColor: tokens.colorSurfaceVariant
        },
        '&:focus': {
          backgroundColor: tokens.colorSurfaceVariant
        },
        '&$selected': {
          '&& svg': {
            display: 'initial !important'
          },
          backgroundColor: tokens.colorSurfaceVariant,
          '&:hover': {
            backgroundColor: tokens.colorSurfaceVariant
          },
          '&:focus': {
            backgroundColor: tokens.colorSurfaceVariant
          }
        }
      }
    },
    MuiTableBody: {
      root: {
        '&& th': {
          padding: '0px !important',
          paddingLeft: '5px !important'
        },
        '&& td': {
          padding: '0px !important',
          paddingLeft: '5px !important'
        }
      }
    },
    MuiTableCell: {
      root: {
        padding: '6px 24px 6px 16px',
        fontSize: '14px !important',
        whiteSpace: 'nowrap'
      }
    },
    MuiButton: {
      root: {
        borderRadius: 2,
        minHeight: 46,
        color: tokens.colorOnSurface,
        '&[data-action-list-button]': {
          border: `1px solid ${tokens.colorSurfaceVariant}`,
          borderRadius: 4,
          justifyContent: 'space-between',
          paddingLeft: 10,
          '& span': {
            fontWeight: 400,
            fontSize: DEFAULT_FONT_SIZE,
            textTransform: 'none'
          },
          '&:hover': {
            backgroundColor: '#fff',
            boxShadow: `0px 2px 4px #B9C0C7`,
            '&&&:before': {
              borderBottom: 'none'
            },
            '&&:after': {
              borderBottom: 'none'
            }
          },
          '&:focus': {
            backgroundColor: '#fff',
            borderColor: tokens.colorPrimary,
            boxShadow: `0px 2px 4px #B9C0C7`
          },
          '&&&:before': {
            borderBottom: 'none'
          },
          '&&:after': {
            borderBottom: 'none'
          },
          '&$disabled': {
            boxShadow: `none`
          }
        }
      },
      outlined: {
        backgroundColor: tokens.colorSurface,
        '&:hover': {
          backgroundColor: tokens.colorSurfaceVariant
        }
      },
      outlinedPrimary: {
        color: tokens.colorPrimary,
        '&$disabled': {
          borderColor: tokens.colorSurfaceVariant,
          color: tokens.colorPrimaryContainer
        }
      },
      contained: {
        backgroundColor: tokens.colorSurface,
        boxShadow: 'none',
        color: tokens.colorOnSurface,
        '&:hover': {
          backgroundColor: tokens.colorSurfaceVariant
        }
      },
      containedPrimary: {
        backgroundColor: tokens.colorPrimary,
        '&$disabled': {
          backgroundColor: tokens.colorPrimaryContainer,
          color: 'white'
        }
      },
      textPrimary: {
        '&$disabled': {
          color: tokens.colorPrimaryContainer
        }
      }
    },
    MuiInputBase: {
      root: {
        fontSize: DEFAULT_FONT_SIZE
      },
      input: {
        padding: '8px 0 7px'
      }
    },
    MuiLink: {
      root: {
        padding: '8px 0 7px'
      }
    },
    MuiInput: {
      formControl: {
        'label + &': {
          marginTop: '8px'
        }
      },
      input: {
        '&::placeholder': {
          fontWeight: 'normal',
          color: tokens.colorOnSurfaceVariant,
          opacity: 1,
          fontSize: DEFAULT_FONT_SIZE
        }
      },

      underline: {
        border: `1px solid ${tokens.colorSurfaceVariant}`,
        '&:hover': {
          boxShadow: `0px 2px 4px #B9C0C7`,
          '&&&:before': {
            borderBottom: 'none'
          },
          '&&:after': {
            borderBottom: 'none'
          }
        },
        '&$focused': {
          borderColor: tokens.colorPrimary,
          boxShadow: `0px 2px 4px #B9C0C7`
        },
        '&&&:before': {
          borderBottom: 'none'
        },
        '&&:after': {
          borderBottom: 'none'
        },
        '&$disabled': {
          boxShadow: `none`
        }
      },
      root: {
        background: '#fff',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 4,
        padding: '6px 0 6px 10px',
        '&$fullWidth': {
          width: '100%'
        },
        border: `1px solid ${tokens.colorSurfaceVariant}`,
        '&$disabled': {
          backgroundColor: tokens.colorSurfaceVariant,
          borderColor: tokens.colorSurfaceVariant
        },
        '&:hover': {
          boxShadow: `0px 2px 4px #B9C0C7`,
          '&&&:before': {
            borderBottom: 'none'
          },
          '&&:after': {
            borderBottom: 'none'
          }
        },
        '&$focused': {
          borderColor: tokens.colorPrimary,
          boxShadow: `0px 2px 4px #B9C0C7`
        }
      }
    },
    MuiInputLabel: MuiLabel,
    MuiFormLabel: MuiLabel,
    MuiPickersToolbarText: {
      toolbarTxt: {
        backgroundColor: 'inherit'
      }
    }
  } as MuiPickersOverrides & Overrides
});
